import {
  placeInBand,
  type SnapshotCondition,
  type SnapshotDetailRange,
  type SnapshotDetailReading,
  type SnapshotDrill as SnapshotDrillData,
  type SnapshotFinding,
  type SnapshotLevel,
  snapshotConditionsSay,
  snapshotDayHeading,
  snapshotDayLabel,
  snapshotDefinition,
  snapshotReadingAtDay,
  snapshotReadingDaysBack,
  snapshotReadingHasDays,
  snapshotReadingLatestDay,
} from "@healthos/shared";
import { formatHoursMinutes } from "@healthos/utils";
import {
  ArrowDownRight,
  ArrowsHorizontal,
  ArrowUpRight,
  CalendarCheck,
  CaretLeft,
  CaretRight,
  CheckCircle,
  Flower,
  Gauge,
  Hourglass,
  Moon,
  Sun,
  SunHorizon,
  Target,
  Thermometer,
  Wind,
} from "@phosphor-icons/react";
import { type CSSProperties, type ReactNode, useMemo, useState } from "react";
import { renderInlineMarkdown } from "./inline-markdown";
import { SnapshotFindings } from "./snapshot-findings";
import { SnapshotHypnogram, SnapshotZones } from "./snapshot-hypnogram";
import { SnapshotLevelCard } from "./snapshot-level";
import { type ReadingParts, Termed } from "./snapshot-term";
import { SnapshotTimeline } from "./snapshot-timeline";
import { TrendChart } from "./trend-chart";

// Positional, never evaluative: the chip says where the reading sits against
// the member's own history and leaves whether that is good to the copy.
const VERDICT: Record<
  NonNullable<SnapshotDetailReading["verdict"]>,
  { label: string; Icon: typeof CheckCircle }
> = {
  in: { label: "In your usual", Icon: CheckCircle },
  under: { label: "Under your usual", Icon: ArrowDownRight },
  over: { label: "Over your usual", Icon: ArrowUpRight },
};

/**
 * What the big number is, in the member's terms: their own target where they
 * have set one, otherwise the reading and the window it was taken over. A drill
 * that grades nothing says so rather than implying a bar it does not have.
 */
function ReadingCaption({ drill }: { drill: SnapshotDrillData }) {
  if (drill.pillar === null) return <>not scored</>;
  // The reading is named on both branches. Naming it only where there is no
  // target left the one drill that has one, movement, showing a bare 1,453.
  // The name carries its definition, the same as every other term on the page.
  const term = (
    <Termed label={drill.headline.label} definition={snapshotDefinition(drill.headline.key)} />
  );
  return drill.trend.target ? (
    <>
      {term}, {drill.trend.target.label} target
    </>
  ) : (
    <>
      {term}, {drill.window.toLowerCase()}
    </>
  );
}

function Verdict({ verdict }: { verdict: SnapshotDetailReading["verdict"] }) {
  if (!verdict) return null;
  const { label, Icon } = VERDICT[verdict];
  return (
    <span className={`snap-verdict is-${verdict}`}>
      <Icon size={13} weight="bold" aria-hidden="true" />
      {label}
    </span>
  );
}

function ContributorRow({ r }: { r: SnapshotDetailReading }) {
  const off = r.value === null;
  const definition = snapshotDefinition(r.key);
  const place = r.value !== null && r.band ? placeInBand(r.value, r.band) : null;
  // A running total is placed against the band but never judged against it:
  // the dot shows where the day has got to, and says nothing about where it
  // will end.
  const open = r.inProgress === true;
  const label = off
    ? `${r.label}, not measured`
    : r.bandDisplay
      ? open
        ? `${r.display} so far today, against a usual full day of ${r.bandDisplay}`
        : `${r.display}, ${r.verdict === "in" ? "inside" : "outside"} your usual range of ${r.bandDisplay}`
      : `${r.display}, usual range not established yet`;
  return (
    <li
      className={`snap-cb${off ? " is-off" : ""}${r.verdict && r.verdict !== "in" ? " is-out" : ""}`}
    >
      <span className="snap-cb-k">
        <Termed label={r.label} definition={definition} />
      </span>
      <span className="snap-cb-v">{off ? "Not measured" : r.display}</span>
      <span className="snap-cb-cell">
        <span className={`snap-cb-pos${place ? "" : " is-empty"}`} role="img" aria-label={label}>
          {place ? (
            <>
              <i
                className="snap-cb-usual"
                style={{ left: `${place.lo}%`, right: `${100 - place.hi}%` }}
              />
              <b
                className={`snap-cb-dot${place.out && !open ? " is-out" : ""}`}
                style={{ left: `${place.pos}%` }}
              />
            </>
          ) : null}
        </span>
        <span className="snap-cb-cap">{r.bandDisplay ?? (off ? "" : "no range yet")}</span>
      </span>
    </li>
  );
}

/** Which day the breakdown is reading. */
function DayCursor({
  daysAgo,
  from,
  back,
  label,
  onChange,
}: {
  daysAgo: number;
  /** The newest day that reported; the cursor never steps past it. */
  from: number;
  back: number;
  label: string;
  onChange: (next: number) => void;
}) {
  if (back <= from) return null;
  return (
    <div className="snap-cur">
      <button
        type="button"
        className="snap-cur-btn"
        disabled={daysAgo >= back}
        aria-label="Show the day before"
        onClick={() => onChange(Math.min(back, daysAgo + 1))}
      >
        <CaretLeft size={13} weight="bold" aria-hidden="true" />
      </button>
      <span className="snap-cur-k" aria-live="polite">
        {label}
      </span>
      <button
        type="button"
        className="snap-cur-btn"
        disabled={daysAgo <= from}
        aria-label="Show the day after"
        onClick={() => onChange(Math.max(from, daysAgo - 1))}
      >
        <CaretRight size={13} weight="bold" aria-hidden="true" />
      </button>
    </div>
  );
}

/**
 * The load side of the week. No wearable reads any of it, so this is the part
 * of a hard week a ring cannot see. Titles are never read, only durations.
 */
function Calendar({ calendar }: { calendar: NonNullable<SnapshotDrillData["calendar"]> }) {
  return (
    <section className="snap-card">
      <div className="snap-section-head">
        <h3>What was in your calendar</h3>
        <span className="hint">Last 7 days</span>
      </div>
      {calendar.say ? <p className="snap-card-say">{calendar.say}</p> : null}
      <ul className="snap-facts">
        <li>
          <span className="snap-facts-i">
            <CalendarCheck size={15} aria-hidden="true" />
          </span>
          <span className="snap-facts-k">
            Booked
            <em>across {calendar.days} days</em>
          </span>
          <b className="snap-facts-v">{formatHoursMinutes(calendar.bookedMinutes / 60)}</b>
        </li>
        <li>
          <span className="snap-facts-i">
            <ArrowsHorizontal size={15} aria-hidden="true" />
          </span>
          <span className="snap-facts-k">
            Back to back
            <em>
              {calendar.longestRunMinutes > 0
                ? `longest stretch ${formatHoursMinutes(calendar.longestRunMinutes / 60)}`
                : "meetings with no gap"}
            </em>
          </span>
          <b className="snap-facts-v">{calendar.backToBack}</b>
        </li>
        <li>
          <span className="snap-facts-i">
            <Hourglass size={15} aria-hidden="true" />
          </span>
          <span className="snap-facts-k">
            Unbooked
            <em>
              {calendar.longestFreeMinutes > 0
                ? `longest block ${formatHoursMinutes(calendar.longestFreeMinutes / 60)}`
                : "between meetings"}
            </em>
          </span>
          <b className="snap-facts-v">{formatHoursMinutes(calendar.freeMinutes / 60)}</b>
        </li>
      </ul>
    </section>
  );
}

const CONDITION_ICON: Record<SnapshotCondition["icon"], typeof Thermometer> = {
  temperature: Thermometer,
  night: Moon,
  pollen: Flower,
  air: Wind,
  pressure: Gauge,
  sunset: SunHorizon,
  daylight: Sun,
  uv: Sun,
};

/** The week's weather, stated as context. It is never given as a cause. */
export function SnapshotConditions({
  items,
  tab,
}: {
  items: ReadonlyArray<SnapshotCondition>;
  /** Only the conditions with a bearing on this tab are shown. */
  tab: string;
}) {
  const shown = items.filter((c) => c.tabs.includes(tab));
  if (shown.length === 0) return null;
  const say = snapshotConditionsSay(items, tab);
  return (
    <section className="snap-env">
      <div className="snap-section-head">
        <h3>Conditions this week</h3>
        <span className="hint">Open-Meteo · not scored</span>
      </div>
      {say ? <p className="snap-card-say">{say}</p> : null}
      <ul className="snap-facts">
        {shown.map((c) => {
          const Icon = CONDITION_ICON[c.icon];
          return (
            <li key={c.label}>
              <span className="snap-facts-i">
                <Icon size={15} aria-hidden="true" />
              </span>
              <span className="snap-facts-k">
                <Termed label={c.label} definition={snapshotDefinition(c.key)} />
                <em>{c.note}</em>
              </span>
              <b className="snap-facts-v">{c.value}</b>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

export interface SnapshotDrillProps {
  drill: SnapshotDrillData;
  /** The behaviour's score and its hue, when this drill grades one. */
  score?: { value: number; tone: string } | null;
  /** The week's plain-English line for this behaviour, from the row narrative. */
  say: string | null;
  days: SnapshotDetailRange;
  onDays: (next: SnapshotDetailRange) => void;
  /** The behaviour's level, when this drill grades one. */
  level?: SnapshotLevel | null;
  /** Rendered under the headline; the strain drill uses it for the step goal. */
  aside?: ReactNode;
  /** The week's weather, stated under the drill as context.  */
  conditions?: ReadonlyArray<SnapshotCondition>;
  /** The trend this behaviour's level exists to move. */
  evidence?: ReadingParts | null;
  /** Opens the level in full. */
  onOpenLevel?: () => void;
  /**
   * Offered where a drill grades no behaviour, so the space a level would
   * take says how the member fills the readings in instead of apologising
   * for a goal that can never attach.
   */
  contribute?: ReactNode;
  /**
   * The findings that bear on this drill. They sit under the habit because
   * they are the reason for it: the member's own days saying what moves this
   * reading, and each one carries the move to make.
   */
  findings?: ReadonlyArray<SnapshotFinding>;
}

export function SnapshotDrill({
  drill,
  say,
  score,
  days,
  onDays,
  level,
  aside,
  conditions = [],
  evidence,
  onOpenLevel,
  contribute,
  findings = [],
}: SnapshotDrillProps) {
  // Opens on the newest day that reported, which for a nightly measure is not today:
  const [cursor, setCursor] = useState<number | null>(null);
  // The drill filters its own so it can tell "none yet" from "none here".
  const mine = findings.filter((f) => f.drill === drill.id);
  const readings = useMemo(
    () => [drill.headline, ...drill.contributors],
    [drill.headline, drill.contributors],
  );
  const back = useMemo(() => snapshotReadingDaysBack(readings), [readings]);
  // The drill's own reading decides the day, not whichever contributor happens to have reported first.
  const from = useMemo(() => snapshotReadingLatestDay([drill.headline]), [drill.headline]);
  // A drill whose readings run shorter than the cursor was left on keeps the
  // cursor inside its own week rather than reading an absent day.
  const at = Math.min(Math.max(cursor ?? from, from), back);
  // A reading taken less often than weekly has nothing inside the cursor's window, and resolving it through that window turned the body drill's own weight into "Not measured" under a column headed Today.
  const hasDays = useMemo(() => snapshotReadingHasDays(readings), [readings]);
  const h = snapshotReadingAtDay(drill.headline, at);
  const contributors = useMemo(
    () => drill.contributors.map((r) => snapshotReadingAtDay(r, at)),
    [drill.contributors, at],
  );
  // The cursor names the day it steps to; the heading names what the column is,
  // which on the newest reported day is the drill's own window.
  const dayLabel = snapshotDayLabel(drill.days[at]?.day, at);
  const dayHeading = snapshotDayHeading(drill, at, from, hasDays);
  return (
    <div className="snap-drill">
      <div className="snap-drill-top">
        {/* The behaviour's hue tints the readings column only. The chart keeps
            the neutral teal so thirty day-columns do not shout the hue that a
            single score panel states once. */}
        <div
          className="snap-contrib"
          style={score ? ({ ["--tone" as string]: score.tone } as CSSProperties) : undefined}
        >
          {/* The drill leads with the measurement, not the 0-100 score: the
              overview row already carried the score, and "show me the numbers"
              is what a member came here for. One line under the reading names
              what the number is, so it is never a bare figure. */}
          <div className="snap-ds">
            <span className="snap-ds-k">{drill.label}</span>
            <span className="snap-ds-v">{h.display ?? "Not measured"}</span>
            <span className="snap-ds-target">
              <ReadingCaption drill={drill} />
            </span>
            <Verdict verdict={h.verdict} />
          </div>
          {say ? <p className="snap-contrib-say">{renderInlineMarkdown(say)}</p> : null}
          {aside}
          <div className="snap-section-head">
            <h3>Your readings</h3>
            <DayCursor daysAgo={at} from={from} back={back} label={dayLabel} onChange={setCursor} />
          </div>
          <div className="snap-cb-head" aria-hidden="true">
            <span>Reading</span>
            <span>{dayHeading}</span>
            <span>Your usual</span>
          </div>
          <ul className="snap-cb-list" key={at}>
            {contributors.map((r) => (
              <ContributorRow key={r.key} r={r} />
            ))}
          </ul>
        </div>
        {/* What the member is working on comes before the history of it: the
            level names the behaviour the chart is evidence for, so it reads
            first and the trend answers it. */}
        <div className="snap-contrib-focus">
          {level ? (
            <div className="snap-drill-level">
              <div className="snap-section-head">
                <h3>
                  {level.label}, level {level.level}
                </h3>
              </div>
              <SnapshotLevelCard level={level} evidence={evidence} onOpen={onOpenLevel} />
            </div>
          ) : (
            contribute
          )}
          {/* The case for the habit, next to the habit. These were computed
              with a drill on each one and only ever rendered on the overview,
              so the tab a member opens to ask why never carried the answer.
              A drill that grades a behaviour keeps the block when it has none,
              since its own empty state says what a comparison still needs. */}
          {drill.pillar ? (
            <SnapshotFindings
              findings={mine}
              foundElsewhere={mine.length === 0 && findings.length > 0}
              showGoingWell={false}
            />
          ) : null}
          <TrendChart trend={drill.trend} days={days} onDays={onDays} />
        </div>
      </div>
      {/* A distribution across the whole night or session needs the full
          width, so it takes a band of its own above the two-up band. */}
      {drill.hypnogram || drill.zones ? (
        <div className="snap-lower is-solo">
          {drill.hypnogram ? <SnapshotHypnogram hypnogram={drill.hypnogram} /> : null}
          {drill.zones ? <SnapshotZones zones={drill.zones} /> : null}
        </div>
      ) : null}
      {/* What explains this drill sits with it, rather than only in one pile
          at the foot of the page. The day breakdown leads, the conditions
          follow it, and the calendar comes last where the tab has one. */}
      <div className="snap-lower">
        <SnapshotTimeline days={drill.days} label={drill.trend.label} />
        <SnapshotConditions items={conditions} tab={drill.id} />
        {drill.calendar ? <Calendar calendar={drill.calendar} /> : null}
      </div>
      <details className="snap-all">
        <summary>
          <CaretRight size={13} weight="bold" aria-hidden="true" />
          All {drill.contributors.length} readings
        </summary>
        <table className="snap-tbl">
          <thead>
            <tr>
              <th scope="col">Reading</th>
              <th scope="col" className="is-num">
                Latest
              </th>
              <th scope="col" className="is-num">
                Your usual
              </th>
              <th scope="col">Source</th>
            </tr>
          </thead>
          <tbody>
            {drill.contributors.map((r) => (
              <tr key={r.key} className={r.value === null ? "is-off" : ""}>
                <th scope="row">{r.label}</th>
                <td className="is-num">{r.display ?? "Not measured"}</td>
                <td className="is-num">{r.bandDisplay ?? "No range yet"}</td>
                <td>{r.source ?? (r.value === null ? "No device" : "")}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </details>
    </div>
  );
}

export function StepGoalAside({ goal, onOpen }: { goal: number; onOpen: () => void }) {
  return (
    <button
      type="button"
      className="snap-step-goal-trigger"
      onClick={onOpen}
      aria-label="Adjust step goal"
    >
      <Target size={13} weight="bold" />
      <span>Goal {goal.toLocaleString()}</span>
    </button>
  );
}
